import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FcPaginatorComponent } from '@core/components/fc-paginator/fc-paginator.component';
import { Group, Location, PagedResult } from '@core/interfaces';
import { LocationService } from 'app/main/locations/services/location.service';
import { chunk } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'link-groups',
  templateUrl: './link-groups.component.html',
  styleUrls: ['../tab-groups.component.scss']
})
export class LinkGroupsComponent implements OnInit, OnDestroy {
  @ViewChild('form', { read: ElementRef, static: true })
  public form: ElementRef<HTMLDivElement>;

  @ViewChild('paginator', { static: false })
  public paginator: FcPaginatorComponent;

  public page: PagedResult<Group> = null;
  public searchInput = new FormControl();
  public isLoading = true;
  public isSaving = false;
  public isAllSelected = false;
  public currentPage = 0;
  public pageSize = 5;
  public isPageChanging = false;
  public groupsToAdd = new Map<string, boolean>();
  private location: Location;

  private readonly reload = new Subject<void>();
  private readonly removeSubscriptions = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialogRef: MatDialogRef<any>,
    private readonly locationService: LocationService,
  ) {
    this.location = data.location;
  }

  public ngOnInit(): void {

    this.searchInput.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.isLoading = true;
      if (this.paginator) {
        this.paginator.firstPage();
      } else {
        this.reload.next();
      }
    });

    this.reload.pipe(
      takeUntil(this.removeSubscriptions),
      tap(() => this.isLoading = true),
      switchMap(() => this.searchGroups()),
      tap(() => {
        this.isLoading = false;
      })
    ).subscribe((page) => {
      this.page = page;
      this.form.nativeElement.scrollTo({ top: 0 });
    });

    this.reload.next();
  }

  public ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  public toggleSegmentLinkedToGroup(checked: boolean, group: Group): void {
    if (this.groupsToAdd.has(group.id)) {
      this.groupsToAdd.delete(group.id);
    } else {
      this.groupsToAdd.set(group.id, checked);
    }

    this.isAllSelected = this.checkIfIsAllSelected();
  }

  private async searchGroups(): Promise<PagedResult<Group>> {
    return this.locationService.listGroups({
      locationId: this.location.id,
      limit: this.pageSize,
      offset: this.currentPage * this.pageSize,
      where: {
        name_contains: this.searchInput.value,
      }
    });
  }

  public toggleAllSelected(checked: boolean): void {
    const groups = (this.page && this.page.items) || [];
    for (const group of groups) {
      if (!group.locationsConnection.items.length) {
        this.toggleSegmentLinkedToGroup(checked, group);
      }
    }

    this.isAllSelected = this.checkIfIsAllSelected();
  }

  private checkIfIsAllSelected(): boolean {
    const groups = (this.page && this.page.items) || [];
    return groups.length && groups.every((group) => group.locationsConnection.items.length || this.groupsToAdd.has(group.id));
  }

  public async continue(): Promise<void> {
    this.isSaving = true;
    const keys = Array.from(this.groupsToAdd.keys());
    for (const groupIds of chunk(keys, 100)) {
      await this.locationService.addLocationToGroup({
        locationId: this.location.id,
        groupIds
      });
    }

    this.isSaving = false;
    this.dialogRef.close({
      reload: true
    });
  }

  async onPageChange(event: PageEvent): Promise<void> {
    this.isPageChanging = true;
    this.currentPage = event.pageIndex;
    this.page = await this.searchGroups();
    this.isAllSelected = this.checkIfIsAllSelected();
    this.form.nativeElement.scrollTo({ top: 0 });
    this.isPageChanging = false;
    this.isLoading = false;
  }
}
